import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Resolver, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { joinPhone, signupSchema, SignupSchema, socialSignupSchema } from './authSchema';
import {
  checkEmailAvailable,
  sendEmailCode,
  sendPhoneCode,
  signupUser,
  signupWithSocial,
  SocialSignupRequired,
  verifyEmailCode,
  verifyPhoneCode,
} from './authService';
import { startKakaoLogin, startNaverLogin } from './socialAuth';
import { useUserSession } from './userSession';
import styles from './Auth.module.scss';

const PROVIDER_LABEL = { kakao: '카카오', naver: '네이버' } as const;

function formatRemaining(seconds: number): string {
  return seconds > 0 ? `(${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')})` : '(만료됨)';
}

// 카카오/네이버 인증 후 넘어온 경우: 비밀번호 없이 이름/휴대폰번호만 받아 가입을 마친다.
// 일반 가입으로 전환하면 폼 상태가 남지 않도록 key로 새로 마운트한다
function Signup() {
  const location = useLocation();
  const socialSignup = (location.state as { socialSignup?: SocialSignupRequired } | null)?.socialSignup ?? null;
  return <SignupForm key={socialSignup?.signupToken ?? 'email'} socialSignup={socialSignup} />;
}

function SignupForm({ socialSignup }: { socialSignup: SocialSignupRequired | null }) {
  const navigate = useNavigate();
  const providerEmail = socialSignup?.email ?? null;
  const [session, setSession] = useUserSession();
  const [serverError, setServerError] = useState<string | null>(null);
  const [socialLoading, setSocialLoading] = useState<'kakao' | 'naver' | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    trigger,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<SignupSchema>({
    resolver: (socialSignup ? zodResolver(socialSignupSchema) : zodResolver(signupSchema)) as Resolver<SignupSchema>,
    defaultValues: { email: providerEmail ?? '' },
  });

  // 이메일 중복확인/인증 상태. 인증 후 이메일을 바꾸면 다시 인증해야 한다
  const [emailNotice, setEmailNotice] = useState<{ ok: boolean; message: string } | null>(null);
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);

  const normalizedEmail = (watch('email') ?? '').trim().toLowerCase();
  // 소셜 계정에서 받은 이메일은 이미 인증된 것으로 본다
  const isEmailVerified = providerEmail !== null || (verifiedEmail !== null && verifiedEmail === normalizedEmail);
  const isCodeSentForEmail = codeSentTo !== null && codeSentTo === normalizedEmail;

  useEffect(() => {
    if (!codeExpiresAt) return undefined;
    const tick = () => setRemainingSeconds(Math.max(0, Math.ceil((codeExpiresAt - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [codeExpiresAt]);

  // 간편가입 휴대폰 인증 상태. 인증 후 번호를 바꾸면 다시 인증해야 한다
  const [phoneNotice, setPhoneNotice] = useState<{ ok: boolean; message: string } | null>(null);
  const [phoneCodeSentTo, setPhoneCodeSentTo] = useState<string | null>(null);
  const [phoneCodeExpiresAt, setPhoneCodeExpiresAt] = useState<number | null>(null);
  const [phoneRemainingSeconds, setPhoneRemainingSeconds] = useState(0);
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneCodeError, setPhoneCodeError] = useState<string | null>(null);
  const [verifiedPhone, setVerifiedPhone] = useState<string | null>(null);
  const [isSendingPhoneCode, setIsSendingPhoneCode] = useState(false);
  const [isVerifyingPhoneCode, setIsVerifyingPhoneCode] = useState(false);

  const currentPhone = joinPhone({
    phone1: watch('phone1') ?? '',
    phone2: watch('phone2') ?? '',
    phone3: watch('phone3') ?? '',
  });
  const isPhoneVerified = verifiedPhone !== null && verifiedPhone === currentPhone;
  const isPhoneCodeSentForPhone = phoneCodeSentTo !== null && phoneCodeSentTo === currentPhone;

  useEffect(() => {
    if (!phoneCodeExpiresAt) return undefined;
    const tick = () => setPhoneRemainingSeconds(Math.max(0, Math.ceil((phoneCodeExpiresAt - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [phoneCodeExpiresAt]);

  const phoneRegisterOptions = {
    onChange: () => {
      setPhoneNotice(null);
      setPhoneCodeError(null);
    },
  };

  const handleSendPhoneCode = async () => {
    // 알림톡에 이름이 들어가므로 이름도 함께 확인한다
    if (!socialSignup || !(await trigger(['name', 'phone1', 'phone2', 'phone3']))) return;
    setPhoneNotice(null);
    setPhoneCodeError(null);
    setIsSendingPhoneCode(true);
    try {
      const { expiresInSeconds } = await sendPhoneCode(socialSignup.signupToken, currentPhone, (watch('name') ?? '').trim());
      setPhoneCodeSentTo(currentPhone);
      setPhoneCodeExpiresAt(Date.now() + expiresInSeconds * 1000);
      setPhoneCode('');
      setPhoneNotice({ ok: true, message: '인증번호를 카카오톡으로 발송했습니다.' });
    } catch (err) {
      setPhoneNotice({ ok: false, message: err instanceof Error ? err.message : '인증번호 발송 중 오류가 발생했습니다.' });
    } finally {
      setIsSendingPhoneCode(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    if (!/^\d{6}$/.test(phoneCode)) {
      setPhoneCodeError('인증번호 6자리를 입력해주세요.');
      return;
    }
    setPhoneCodeError(null);
    setIsVerifyingPhoneCode(true);
    try {
      await verifyPhoneCode(currentPhone, phoneCode);
      setVerifiedPhone(currentPhone);
      setPhoneCodeSentTo(null);
      setPhoneCodeExpiresAt(null);
      setPhoneNotice({ ok: true, message: '휴대폰 인증이 완료되었습니다.' });
      clearErrors(['phone1', 'phone2', 'phone3']);
    } catch (err) {
      setPhoneCodeError(err instanceof Error ? err.message : '인증번호 확인 중 오류가 발생했습니다.');
    } finally {
      setIsVerifyingPhoneCode(false);
    }
  };

  const emailRegister = register('email', {
    onChange: () => {
      setEmailNotice(null);
      setCodeError(null);
    },
  });

  const handleEmailBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    await emailRegister.onBlur(event);
    if (!normalizedEmail || isEmailVerified || !(await trigger('email'))) return;
    try {
      const { available } = await checkEmailAvailable(normalizedEmail);
      setEmailNotice(
        available
          ? { ok: true, message: '사용 가능한 이메일입니다. 인증번호를 받아주세요.' }
          : { ok: false, message: '이미 가입된 이메일입니다.' },
      );
    } catch (err) {
      setEmailNotice({ ok: false, message: err instanceof Error ? err.message : '이메일 확인 중 오류가 발생했습니다.' });
    }
  };

  const handleSendCode = async () => {
    if (!(await trigger('email'))) return;
    setEmailNotice(null);
    setCodeError(null);
    setIsSendingCode(true);
    try {
      const { expiresInSeconds } = await sendEmailCode(normalizedEmail);
      setCodeSentTo(normalizedEmail);
      setCodeExpiresAt(Date.now() + expiresInSeconds * 1000);
      setCode('');
      setEmailNotice({ ok: true, message: '인증번호를 발송했습니다. 메일함을 확인해주세요.' });
    } catch (err) {
      setEmailNotice({ ok: false, message: err instanceof Error ? err.message : '인증번호 발송 중 오류가 발생했습니다.' });
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!/^\d{6}$/.test(code)) {
      setCodeError('인증번호 6자리를 입력해주세요.');
      return;
    }
    setCodeError(null);
    setIsVerifyingCode(true);
    try {
      await verifyEmailCode(normalizedEmail, code);
      setVerifiedEmail(normalizedEmail);
      setCodeSentTo(null);
      setCodeExpiresAt(null);
      setEmailNotice({ ok: true, message: '이메일 인증이 완료되었습니다.' });
      clearErrors('email');
    } catch (err) {
      setCodeError(err instanceof Error ? err.message : '인증번호 확인 중 오류가 발생했습니다.');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  // 이미 로그인된 상태로 들어오면 마이페이지로 보낸다. 방금 가입한 경우엔 완료 페이지로 가야 하므로 건너뛴다
  const justSignedUpRef = useRef(false);
  useEffect(() => {
    if (session && !justSignedUpRef.current) {
      navigate('/mypage', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const onSubmit = async (values: SignupSchema) => {
    setServerError(null);
    if (!providerEmail && verifiedEmail !== values.email.toLowerCase()) {
      setError('email', { message: '이메일 인증을 완료해주세요.' });
      return;
    }
    if (socialSignup && verifiedPhone !== joinPhone(values)) {
      setError('phone1', { message: '휴대폰 인증을 완료해주세요.' });
      return;
    }
    try {
      const { token, user } = socialSignup
        ? await signupWithSocial(socialSignup.signupToken, values)
        : await signupUser(values);
      justSignedUpRef.current = true;
      setSession({ token, user });
      navigate('/signup/complete', { replace: true, state: { name: user.name } });
    } catch (err) {
      setServerError(err instanceof Error ? err.message : '회원가입 중 오류가 발생했습니다.');
    }
  };

  const handleKakaoLogin = async () => {
    setServerError(null);
    setSocialLoading('kakao');
    try {
      await startKakaoLogin();
    } catch (err) {
      setServerError('카카오 로그인을 시작할 수 없습니다.');
      setSocialLoading(null);
    }
  };

  const handleNaverLogin = async () => {
    setServerError(null);
    setSocialLoading('naver');
    try {
      await startNaverLogin();
    } catch (err) {
      setServerError('네이버 로그인을 시작할 수 없습니다.');
      setSocialLoading(null);
    }
  };

  return (
    <div className={`${styles.page} ${styles.pageCenter}`}>
      <section className={styles.pageHead}>
        <div className={styles.crumb}>
          <Link to="/">HOME</Link>
          <span>/</span>
          <span>회원가입</span>
        </div>
        <h1>회원가입</h1>
        {socialSignup ? (
          <p>
            {PROVIDER_LABEL[socialSignup.provider]} 인증이 완료되었습니다. 이름을 입력하고 휴대폰 인증을 마치면 가입이 완료됩니다.
          </p>
        ) : (
          <p>이메일로 간편하게 가입하고 견적 문의와 주문 조회를 이용해보세요.</p>
        )}
      </section>

      <section className={styles.formCard}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.field}>
            <label htmlFor="signup-email">이메일</label>
            <div className={styles.inputRow}>
              <input
                id="signup-email"
                type="email"
                placeholder="hello@brand.com"
                readOnly={isEmailVerified}
                {...emailRegister}
                onBlur={handleEmailBlur}
              />
              {!providerEmail && (
                <button
                  type="button"
                  className={styles.inlineBtn}
                  onClick={handleSendCode}
                  disabled={isSendingCode || isEmailVerified}
                >
                  {isEmailVerified ? '인증완료' : isSendingCode ? '발송 중...' : isCodeSentForEmail ? '재발송' : '인증번호 받기'}
                </button>
              )}
            </div>
            {providerEmail && (
              <p className={styles.fieldInfo}>{PROVIDER_LABEL[socialSignup!.provider]} 계정의 이메일로 가입합니다.</p>
            )}
            {errors.email && <p className={styles.fieldError}>{errors.email.message}</p>}
            {!errors.email && emailNotice && (
              <p className={emailNotice.ok ? styles.fieldInfo : styles.fieldError}>{emailNotice.message}</p>
            )}
          </div>
          {isCodeSentForEmail && !isEmailVerified && (
            <div className={styles.field}>
              <label htmlFor="signup-email-code">
                인증번호 {formatRemaining(remainingSeconds)}
              </label>
              <div className={styles.inputRow}>
                <input
                  id="signup-email-code"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6자리 숫자"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
                <button
                  type="button"
                  className={styles.inlineBtn}
                  onClick={handleVerifyCode}
                  disabled={isVerifyingCode || remainingSeconds === 0}
                >
                  {isVerifyingCode ? '확인 중...' : '확인'}
                </button>
              </div>
              {codeError && <p className={styles.fieldError}>{codeError}</p>}
            </div>
          )}
          <div className={styles.field}>
            <label htmlFor="signup-name">이름</label>
            <input id="signup-name" placeholder="예) 김윤서" {...register('name')} />
            {errors.name && <p className={styles.fieldError}>{errors.name.message}</p>}
          </div>
          <div className={styles.field}>
            <label htmlFor="signup-phone1">휴대폰번호</label>
            <div className={styles.inputRow}>
              <div className={styles.phoneRow}>
                <input
                  id="signup-phone1"
                  type="tel"
                  placeholder="010"
                  maxLength={3}
                  readOnly={isPhoneVerified}
                  {...register('phone1', phoneRegisterOptions)}
                />
                <input
                  type="tel"
                  placeholder="1234"
                  maxLength={4}
                  readOnly={isPhoneVerified}
                  {...register('phone2', phoneRegisterOptions)}
                />
                <input
                  type="tel"
                  placeholder="5678"
                  maxLength={4}
                  readOnly={isPhoneVerified}
                  {...register('phone3', phoneRegisterOptions)}
                />
              </div>
              {socialSignup && (
                <button
                  type="button"
                  className={styles.inlineBtn}
                  onClick={handleSendPhoneCode}
                  disabled={isSendingPhoneCode || isPhoneVerified}
                >
                  {isPhoneVerified
                    ? '인증완료'
                    : isSendingPhoneCode
                      ? '발송 중...'
                      : isPhoneCodeSentForPhone
                        ? '재발송'
                        : '인증번호 받기'}
                </button>
              )}
            </div>
            {(errors.phone1 || errors.phone2 || errors.phone3) && (
              <p className={styles.fieldError}>
                {errors.phone1?.message || errors.phone2?.message || errors.phone3?.message}
              </p>
            )}
            {!(errors.phone1 || errors.phone2 || errors.phone3) && phoneNotice && (
              <p className={phoneNotice.ok ? styles.fieldInfo : styles.fieldError}>{phoneNotice.message}</p>
            )}
          </div>
          {socialSignup && isPhoneCodeSentForPhone && !isPhoneVerified && (
            <div className={styles.field}>
              <label htmlFor="signup-phone-code">인증번호 {formatRemaining(phoneRemainingSeconds)}</label>
              <div className={styles.inputRow}>
                <input
                  id="signup-phone-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="6자리 숫자"
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, ''))}
                />
                <button
                  type="button"
                  className={styles.inlineBtn}
                  onClick={handleVerifyPhoneCode}
                  disabled={isVerifyingPhoneCode || phoneRemainingSeconds === 0}
                >
                  {isVerifyingPhoneCode ? '확인 중...' : '확인'}
                </button>
              </div>
              {phoneCodeError && <p className={styles.fieldError}>{phoneCodeError}</p>}
            </div>
          )}
          {!socialSignup && (
            <>
              <div className={styles.field}>
                <label htmlFor="signup-password">비밀번호</label>
                <input id="signup-password" type="password" placeholder="4자 이상" {...register('password')} />
                {errors.password && <p className={styles.fieldError}>{errors.password.message}</p>}
              </div>
              <div className={styles.field}>
                <label htmlFor="signup-password-confirm">비밀번호 확인</label>
                <input id="signup-password-confirm" type="password" {...register('passwordConfirm')} />
                {errors.passwordConfirm && <p className={styles.fieldError}>{errors.passwordConfirm.message}</p>}
              </div>
            </>
          )}

          {serverError && <p className={styles.errorBox}>{serverError}</p>}

          <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
            {isSubmitting ? '가입 중...' : '회원가입'}
          </button>
        </form>

        {socialSignup ? (
          <div className={styles.helpLinks}>
            <Link to="/signup" replace>
              다른 방법으로 가입하기
            </Link>
          </div>
        ) : (
          <>
            <div className={styles.socialDivider}>또는</div>
            <div className={styles.socialButtons}>
              <button
                type="button"
                className={`${styles.socialBtn} ${styles.kakaoBtn}`}
                onClick={handleKakaoLogin}
                disabled={socialLoading !== null}
              >
                {socialLoading === 'kakao' ? '이동 중...' : '카카오로 시작하기'}
              </button>
              <button
                type="button"
                className={`${styles.socialBtn} ${styles.naverBtn}`}
                onClick={handleNaverLogin}
                disabled={socialLoading !== null}
              >
                {socialLoading === 'naver' ? '이동 중...' : '네이버로 시작하기'}
              </button>
            </div>
          </>
        )}

        <div className={styles.helpLinks}>
          <Link to="/login">이미 회원이신가요? 로그인</Link>
        </div>
      </section>
    </div>
  );
}

export default Signup;
